import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import StatePicker from "countrycitystatejson";
// @material-ui/core components
import Checkbox from "@mui/material/Checkbox";

import {
  Box,
  Dialog,
  Fab,
  FormControlLabel,
  IconButton,
  makeStyles,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  useMediaQuery,
  useTheme,
  FormHelperText,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
// core components
import GridItem from "../../components/Grid/GridItem.js";
import GridContainer from "../../components/Grid/GridContainer.js";
import CustomInput from "../../components/CustomInput/CustomInput.js";
import Button from "../../components/CustomButtons/Button.js";
import Card from "../../components/Card/Card.js";
import CardHeader from "../../components/Card/CardHeader.js";
import CardBody from "../../components/Card/CardBody.js";
import { Autocomplete } from "@material-ui/lab";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  CreateAccountAPI,
  GetAllAccountsAPI,
  GetAllAccountsWithLightweightAPI,
  GetAllProductMastersAPI,
  GetAllReadyMastersAPI,
} from "../../config/api";
import CardFooter from "../../components/Card/CardFooter";
import Check from "@material-ui/icons/Check";
import DeleteIcon from "@material-ui/icons/Delete";

import styles from "../../../src/assets/jss/material-dashboard-react/checkboxAdnRadioStyle.js";
import { createFilterOptions } from "@material-ui/lab/Autocomplete";
import { toast } from "react-toastify";
import { serializeShippingAddressesForApi } from "../../utils/accountShippingAddress";

const filterOptions = createFilterOptions({
  matchFrom: "any",
  limit: 500,
});

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PIN_RE = /^\d{6}$/;
const requiredLabel = (label) => (
  <>
    {label}
    <span style={{ color: "#f44336" }}> *</span>
  </>
);

function normalizeIndianMobile(s) {
  if (s == null || s === "") return "";
  const d = String(s).replace(/\D/g, "");
  return d.length >= 10 ? d.slice(-10) : d;
}

function isValidIndianMobile(s) {
  const m = normalizeIndianMobile(s);
  return /^[6-9]\d{9}$/.test(m);
}

/**
 * Returns an array of error messages (empty if valid).
 */
function validateAccountDetails(values) {
  const msgs = [];
  const {
    accountCode,
    partyType,
    name,
    address,
    state,
    city,
    pin,
    mobile,
    email,
    whatsappNumber,
  } = values;

  if (!accountCode || !String(accountCode).trim()) {
    msgs.push("A/C Code is required.");
  }
  if (!partyType) {
    msgs.push("Party Type is required.");
  }
  if (!name || !String(name).trim()) {
    msgs.push("Name is required.");
  }
  if (!address || String(address).trim().length < 3) {
    msgs.push("Address is required (at least 3 characters).");
  }
  if (!state) {
    msgs.push("State is required.");
  }
  if (!city) {
    msgs.push("City is required.");
  }
  if (!pin || !PIN_RE.test(String(pin).trim())) {
    msgs.push("Pincode must be exactly 6 digits.");
  }
  if (!isValidIndianMobile(mobile)) {
    msgs.push("Mobile must be a valid 10-digit Indian number.");
  }
  if (email && String(email).trim() && !EMAIL_RE.test(String(email).trim())) {
    msgs.push("Company email must be valid.");
  }
  if (whatsappNumber && String(whatsappNumber).trim()) {
    if (!isValidIndianMobile(whatsappNumber)) {
      msgs.push("WhatsApp number must be a valid 10-digit number.");
    }
  }
  return msgs;
}

function validateAccountUsers(employees) {
  const msgs = [];
  if (!employees || employees.length === 0) {
    msgs.push("Add at least one account user (step 2) with Name, Email, Mobile, and Password.");
    return msgs;
  }
  employees.forEach((row, idx) => {
    const n = idx + 1;
    if (!row.name || !String(row.name).trim()) {
      msgs.push(`User ${n}: Name is required.`);
    }
    if (!row.email || !EMAIL_RE.test(String(row.email).trim())) {
      msgs.push(`User ${n}: Valid email is required.`);
    }
    if (!row.mobile || !isValidIndianMobile(row.mobile)) {
      msgs.push(`User ${n}: Valid 10-digit mobile is required.`);
    }
    if (!row.password || !String(row.password).trim()) {
      msgs.push(`User ${n}: Password is required.`);
    }
  });
  return msgs;
}

let s = _.merge(styles, {
  formControl: {
    paddingBottom: "10px",
    margin: "27px 0 0 0",
    position: "relative",
    verticalAlign: "unset",
  },
  cardCategoryWhite: {
    color: "rgba(255,255,255,.62)",
    margin: "0",
    fontSize: "14px",
    marginTop: "0",
    marginBottom: "0",
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "300",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px",
    textDecoration: "none",
  },
});

const useStyles = makeStyles(s);

function getSteps() {
  return ["Account Details", "Account Users"];
}

const columns = [
  { id: "product", label: "Product" },
  { id: "action", label: "Action" },
];

const accountUsersColumns = [
  { id: "name", label: "Name" },
  { id: "mobile", label: "Mobile" },
  { id: "role", label: "Role" },
  { id: "permissions", label: "Permissions" },
];
// const products = ['Product 1', 'Product 2', 'Product 3', 'Product 4', 'Product 5'];

export default function AccountAdd(props) {
  const classes = useStyles();
  const theme = useTheme();
  let userInfo = useSelector((state) => state.userReducer.userInfo);
  const [activeStep, setActiveStep] = useState(0);
  const steps = getSteps();

  const { open, close, fetchData } = props;
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));

  const [accountCode, setAccountCode] = useState("");
  const [partyType, setPartyType] = useState("");
  const [name, setName] = useState("");
  // const [contact, setContact] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState(null);
  const [state, setState] = useState(null);
  const [pin, setPin] = useState("");
  const [openingBalance, setOpeningBalance] = useState(0)
  const [landline, setLandline] = useState("");
  const [mobile, setMobile] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [email, setEmail] = useState("");
  const [shippingAddress, setShippingAddress] = useState({
    allAddress: [
      { fullname: "", address: "", stateAddress: "", city: "", pincode: "" },
    ],
  });
  const [openBalance, setOpenBalance] = useState("");
  const [currentBalance, setCurrentBalance] = useState("");
  const [lastYearBalance, setLastYearBalance] = useState("");
  const [challanType, setChallanType] = useState("");
  const [showRunningBalance, setShowRunningBalance] = useState(false);
  const [isEligibleForIgst, setIsELigibleForIgst] = useState(false)
  const [showPrice, setShowPrice] = useState(false)
  const [printBulk, setPrintBulk] = useState(false);
  const [openPartySelectionDialog, setOpenPartySelectionDialog] =
    useState(false);
  const [copyDiscount, setCopyDiscount] = useState(false);
  const [cashPreference, setCashPreference] = useState(false);
  const [thresholdBalance, setThresholdBalance] = useState("");
  const [balanceLock, setBalanceLock] = useState("");

  const [accounts, setAccounts] = useState([]);
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState("");
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState(StatePicker.getStatesByShort("IN"));
  const [gstNumber, setGstNumber] = useState("");

  const [employeeName, setEmployeeName] = useState("");
  const [employeeEmail, setEmployeeEmail] = useState("");
  const [employeePassword, setEmployeePassword] = useState("");
  const [employeeMobile, setEmployeeMobile] = useState("");
  const [employeeAddress, setEmployeeAddress] = useState("");
  const [employeeRole, setEmployeeRole] = useState(false);

  const [viewPurchase, setViewPurchase] = useState(false);
  const [viewAccounts, setViewAccounts] = useState(false);
  const [viewAccountsVerification, setViewAccountsVerification] =
    useState(false);
  const [viewDiscounts, setViewDiscounts] = useState(false);
  const [viewOrders, setViewOrders] = useState(false);
  const [viewOrdersApp, setViewOrdersApp] = useState(false);
  const [viewOrderReturn, setViewOrderReturn] = useState(false);
  const [viewTransaction, setViewTransaction] = useState(false);
  const [viewOrderStatus, setViewOrderStatus] = useState(false);
  const [viewPrintAd, setViewPrintAd] = useState(false);
  const [viewBankList, setViewBankList] = useState(false);
  const [viewPaymentList, setViewPaymentList] = useState(false);
  const [viewPurchaseBarcode, setViewPurchaseBarcode] = useState(false);
  const [viewProductBarcode, setViewProductBarcode] = useState(false);
  const [viewOrderPrintPage, setViewOrderPrintPage] = useState(false);
  const [viewBrandsList, setViewBrandsList] = useState(false);
  const [viewOfferList, setViewOfferList] = useState(false);
  const [viewTrendingList, setViewTrendingList] = useState(false);
  const [viewBrochureList, setViewBrochureList] = useState(false);
  const [viewPriceList, setViewPriceList] = useState(false);
  const [viewMaster, setViewMaster] = useState(false);
  const [viewCategoryList, setViewCategoryList] = useState(false);
  const [viewAccounting, setViewAccounting] = useState(false);
  const [viewDashboard, setViewDashboard] = useState(false);
  const [viewSystemUsers, setViewSystemUsers] = useState(false);
  const [viewAccountUsers, setViewAccountUsers] = useState(false);

  const [selectAll, setSelectAll] = useState(false);

  const [employees, setEmployees] = useState([]);

  const authToken = useSelector((state) => state.userReducer.authToken);
  const readyMasters = useSelector((state) => state.userReducer.readyMasters);

  const [touched, setTouched] = useState({});
  const [employeeTouched, setEmployeeTouched] = useState({});

  const markTouched = (key) =>
    setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  const markEmployeeTouched = (key) =>
    setEmployeeTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));

  const fieldErrors = useMemo(() => {
    const errors = {};
    if (!accountCode || !String(accountCode).trim()) errors.accountCode = "A/C Code is required.";
    if (!partyType) errors.partyType = "Party Type is required.";
    if (!name || !String(name).trim()) errors.name = "Name is required.";
    if (!address || String(address).trim().length < 3) errors.address = "Address must be at least 3 characters.";
    if (!state) errors.state = "State is required.";
    if (!city) errors.city = "City is required.";
    if (!pin || !PIN_RE.test(String(pin).trim())) errors.pin = "Pincode must be exactly 6 digits.";
    if (!isValidIndianMobile(mobile)) errors.mobile = "Mobile must be a valid 10-digit Indian number.";
    if (email && String(email).trim() && !EMAIL_RE.test(String(email).trim())) {
      errors.email = "Company email must be valid.";
    }
    if (whatsappNumber && String(whatsappNumber).trim() && !isValidIndianMobile(whatsappNumber)) {
      errors.whatsappNumber = "WhatsApp number must be a valid 10-digit number.";
    }
    return errors;
  }, [
    accountCode,
    partyType,
    name,
    address,
    state,
    city,
    pin,
    mobile,
    email,
    whatsappNumber,
  ]);

  const isStep0Valid = useMemo(() => {
    const requiredKeys = [
      "accountCode",
      "partyType",
      "name",
      "address",
      "state",
      "city",
      "pin",
      "mobile",
    ];
    return requiredKeys.every((k) => !fieldErrors[k]);
  }, [fieldErrors]);

  const employeeFieldErrors = useMemo(() => {
    const errors = {};
    if (!employeeName || !String(employeeName).trim()) errors.employeeName = "Name is required.";
    if (!employeeEmail || !EMAIL_RE.test(String(employeeEmail).trim())) errors.employeeEmail = "Valid email is required.";
    if (!isValidIndianMobile(employeeMobile)) errors.employeeMobile = "Valid 10-digit mobile is required.";
    if (!employeePassword || !String(employeePassword).trim()) errors.employeePassword = "Password is required.";
    return errors;
  }, [employeeName, employeeEmail, employeeMobile, employeePassword]);

  const canAddEmployee =
    !employeeFieldErrors.employeeName &&
    !employeeFieldErrors.employeeEmail &&
    !employeeFieldErrors.employeeMobile &&
    !employeeFieldErrors.employeePassword;

  useEffect(() => {
    if (open) {
      _getProducts();
      _getAccounts();
    } else {
      // Reset state when dialog closes
      setAccounts([]);
    }
  }, [open]);

  useEffect(() => {
    let cities = StatePicker.getCities("IN", state);
    setCities(cities);
  }, [state]);

  async function _getAccounts() {
    try {
      // Use lightweight endpoint for dropdown performance
      let res = await axios.get(GetAllAccountsWithLightweightAPI(userInfo.entity), {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      setAccounts(res.data.accounts || []);
    } catch (err) {
      // Silently fail - accounts list is optional for discount copying
    }
  }

  const [isSubmitting, setIsSubmitting] = useState(false);

  async function _getProducts() {
    try {
      // let res = await axios.get(GetAllReadyMastersAPI());
      setProducts(readyMasters);
    } catch (err) {
      // console.log(err);
      // toast.error(("Something went wrong, Please Contact Admin"), {
      //   position: "top-center",
      //   autoClose: 1500,
      //   hideProgressBar: false,
      //   closeOnClick: true,
      //   pauseOnHover: true,
      //   draggable: true,
      //   progress: undefined,
      //   theme: "light",
      // });
    }
  }

  async function _onSave() {
    if (isSubmitting) return; // Prevent double submission

    const detailErrors = validateAccountDetails({
      accountCode,
      partyType,
      name,
      address,
      state,
      city,
      pin,
      mobile,
      email,
      challanType,
      whatsappNumber,
    });
    if (detailErrors.length) {
      toast.error(detailErrors[0], {
        position: "top-center",
        autoClose: 4000,
      });
      setActiveStep(0);
      return;
    }

    const userErrors = validateAccountUsers(employees);
    if (userErrors.length) {
      toast.error(userErrors[0], {
        position: "top-center",
        autoClose: 4000,
      });
      setActiveStep(1);
      return;
    }

    try {
      setIsSubmitting(true);
      let account = {
        accountCode: accountCode.trim(),
        partyType,
        name: name.trim(),
        address: address.trim(),
        city,
        state,
        pin: pin.trim(),
        landline: landline.trim(),
        mobile: mobile.trim(),
        email: email.trim(),
        cashPreference,
        thresholdBalance,
        balanceLock,
        shippingAddress: serializeShippingAddressesForApi(shippingAddress.allAddress),
        openBalance,
        currentBalance,
        lastYearBalance,
        whatsappNumber: whatsappNumber.trim(),
        challanType,
        isEligibleForIgst,
        showRunningBalance,
        gstNumber: gstNumber.trim(),
        users: [...employees],
        showPrice,
        printBulk,
        entity: userInfo.entity,
      };
      await axios.post(CreateAccountAPI(), account, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      toast.success('Account created successfully!', {
        position: "top-center",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      close();
      fetchData();
    } catch (e) {
      toast.error("Something went wrong, Please Contact Admin", {
        position: "top-center",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  function addShippingAddressInput() {
    setShippingAddress((prev) => ({
      allAddress: [
        ...(prev.allAddress || []),
        { fullname: "", address: "", stateAddress: "", city: "", pincode: "" },
      ],
    }));
  }

  function removeShippingAddressInput(index) {
    setShippingAddress((prev) => ({
      allAddress: (prev.allAddress || []).filter((_, idx) => idx !== index),
    }));
  }

  function updateShippingRow(index, partial) {
    setShippingAddress((prev) => ({
      allAddress: (prev.allAddress || []).map((row, idx) =>
        idx === index ? { ...row, ...partial } : row
      ),
    }));
  }

  const handleNext = () => {
    if (activeStep === 0) {
      // Mark required fields as touched so inline errors show immediately.
      [
        "accountCode",
        "partyType",
        "name",
        "address",
        "state",
        "city",
        "pin",
        "mobile",
      ].forEach(markTouched);
      const errs = validateAccountDetails({
        accountCode,
        partyType,
        name,
        address,
        state,
        city,
        pin,
        mobile,
        email,
        challanType,
        whatsappNumber,
      });
      if (errs.length) {
        toast.error(`Please fix: ${errs.join(" | ")}`, {
          position: "top-center",
          autoClose: 4000,
        });
        return;
      }
    }
    if (activeStep === 1) {
      const errs = validateAccountUsers(employees);
      if (errs.length) {
        toast.error(errs[0], {
          position: "top-center",
          autoClose: 4000,
        });
        return;
      }
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const PartySelectionDialog = (props) => {
    const [selectedParty, setSelectedParty] = useState(null);

    function _onSave() {
      if (!selectedParty) {
        return;
      }
      setSelectedParty(null);
      setOpenPartySelectionDialog(false);
    }

    return (
      <Dialog
        fullScreen={fullScreen}
        maxWidth={"sm"}
        fullWidth
        open={openPartySelectionDialog}
        onClose={() => setOpenPartySelectionDialog(false)}
        aria-labelledby="responsive-dialog-title"
      >
        <GridContainer>
          <GridItem xs={12} sm={12}>
            <Card>
              <CardHeader color="primary">
                <h4 className={classes.cardTitleWhite}>
                  Copy Discount from Party
                </h4>
              </CardHeader>
              <CardBody>
                <GridItem xs={12} sm={12} md={12}>
                  <Autocomplete
                    id="party"
                    className={classes.formControl}
                    options={accounts}
                    value={selectedParty}
                    onChange={(event, value) => {
                      setSelectedParty(value);
                    }}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Party" />
                    )}
                  />
                </GridItem>
              </CardBody>
              <CardFooter style={{ justifyContent: "center" }}>
                <Button color="primary" onClick={() => _onSave()}>
                  Save
                </Button>
              </CardFooter>
            </Card>
          </GridItem>
        </GridContainer>
      </Dialog>
    );
  };
  return (
    <Dialog
      // fullScreen={fullScreen}
      open={open}
      fullScreen
      onClose={close}
      aria-labelledby="responsive-dialog-title"
    >
      <div style={{ padding: 40, backgroundColor: "#eeeeee" }}>
        <Card>
          <CardHeader color="primary">
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <h4 className={classes.cardTitleWhite}>Account Detail</h4>
              <Button color={"transparent"} onClick={close}>
                Close
              </Button>
            </Box>
          </CardHeader>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {activeStep === 0 ? (
            <>
              <CardBody>
                <GridContainer spacing={4}>
                  <GridItem xs={12} sm={12} md={2}>
                    <CustomInput
                      labelText={requiredLabel("A/C Code")}
                      id="accountCode"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      error={Boolean(touched.accountCode && fieldErrors.accountCode)}
                      inputProps={{
                        value: accountCode,
                        onBlur: () => markTouched("accountCode"),
                        onChange: (event) => {
                          setAccountCode(event.target.value);
                          if (touched.accountCode) markTouched("accountCode");
                        },
                      }}
                    />
                    {touched.accountCode && fieldErrors.accountCode ? (
                      <FormHelperText error>{fieldErrors.accountCode}</FormHelperText>
                    ) : null}
                  </GridItem>
                  <GridItem xs={12} sm={12} md={2}>
                    <Autocomplete
                      id="partyType"
                      className={classes.formControl}
                      options={["sale", "purchase"]}
                      value={partyType}
                      onChange={(event, value) => {
                        setPartyType(value);
                      }}
                      getOptionLabel={(option) => (option ? option : "")}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={requiredLabel("Party Type")}
                          required
                          onBlur={() => markTouched("partyType")}
                          error={Boolean(touched.partyType && fieldErrors.partyType)}
                          helperText={touched.partyType ? fieldErrors.partyType : ""}
                        />
                      )}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={2}>
                    <CustomInput
                      labelText={requiredLabel("Name")}
                      id="name"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      error={Boolean(touched.name && fieldErrors.name)}
                      inputProps={{
                        value: name,
                        onBlur: () => markTouched("name"),
                        onChange: (event) => setName(event.target.value),
                      }}
                    />
                    {touched.name && fieldErrors.name ? (
                      <FormHelperText error>{fieldErrors.name}</FormHelperText>
                    ) : null}
                  </GridItem>
                  {/* <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      labelText="Contact"
                      id="contact"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      inputProps={{
                        value: contact,
                        onChange: (event) => setContact(event.target.value),
                      }}
                    />
                  </GridItem> */}
                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      labelText={requiredLabel("Address")}
                      id="address"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      error={Boolean(touched.address && fieldErrors.address)}
                      inputProps={{
                        multiline: true,
                        value: address,
                        onBlur: () => markTouched("address"),
                        onChange: (event) => setAddress(event.target.value),
                      }}
                    />
                    {touched.address && fieldErrors.address ? (
                      <FormHelperText error>{fieldErrors.address}</FormHelperText>
                    ) : null}
                  </GridItem>
                  <GridItem xs={12} sm={12} md={2}>
                    <Autocomplete
                      id="state"
                      className={classes.formControl}
                      options={states}
                      value={state}
                      onChange={(event, value) => {
                        setState(value);
                      }}
                      getOptionLabel={(option) => (option ? option : "")}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={requiredLabel("Select State")}
                          required
                          onBlur={() => markTouched("state")}
                          error={Boolean(touched.state && fieldErrors.state)}
                          helperText={touched.state ? fieldErrors.state : ""}
                        />
                      )}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={2}>
                    <Autocomplete
                      id="city"
                      className={classes.formControl}
                      options={cities}
                      value={city}
                      onChange={(event, value) => {
                        setCity(value);
                      }}
                      getOptionLabel={(option) => (option ? option : "")}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={requiredLabel("Select City")}
                          required
                          onBlur={() => markTouched("city")}
                          error={Boolean(touched.city && fieldErrors.city)}
                          helperText={touched.city ? fieldErrors.city : ""}
                        />
                      )}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={2}>
                    <CustomInput
                      labelText={requiredLabel("Pincode")}
                      id="pin"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      error={Boolean(touched.pin && fieldErrors.pin)}
                      inputProps={{
                        value: pin,
                        onBlur: () => markTouched("pin"),
                        onChange: (event) => setPin(event.target.value),
                      }}
                    />
                    {touched.pin && fieldErrors.pin ? (
                      <FormHelperText error>{fieldErrors.pin}</FormHelperText>
                    ) : null}
                  </GridItem>


                  <GridItem xs={12} sm={12} md={2}>
                    <CustomInput
                      labelText="Landline"
                      id="landline"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      inputProps={{
                        value: landline,
                        onChange: (event) => setLandline(event.target.value),
                      }}
                    />
                  </GridItem>

                  <GridItem xs={12} sm={12} md={2}>
                    <CustomInput
                      labelText={requiredLabel("Mobile")}
                      id="mobile"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      error={Boolean(touched.mobile && fieldErrors.mobile)}
                      inputProps={{
                        value: mobile,
                        onBlur: () => markTouched("mobile"),
                        onChange: (event) => setMobile(event.target.value),
                      }}
                    />
                    {touched.mobile && fieldErrors.mobile ? (
                      <FormHelperText error>{fieldErrors.mobile}</FormHelperText>
                    ) : null}
                  </GridItem>
                  <GridItem xs={12} sm={12} md={2}>
                    <CustomInput
                      labelText="Whatsapp Number"
                      id="mobile"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      error={Boolean(touched.whatsappNumber && fieldErrors.whatsappNumber)}
                      inputProps={{
                        value: whatsappNumber,
                        onBlur: () => markTouched("whatsappNumber"),
                        onChange: (event) =>
                          setWhatsappNumber(event.target.value),
                      }}
                    />
                    {touched.whatsappNumber && fieldErrors.whatsappNumber ? (
                      <FormHelperText error>{fieldErrors.whatsappNumber}</FormHelperText>
                    ) : null}
                  </GridItem>
                  <GridItem xs={12} sm={12} md={3}>
                    <CustomInput
                      labelText="Company Email"
                      id="email"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      error={Boolean(touched.email && fieldErrors.email)}
                      inputProps={{
                        value: email,
                        onBlur: () => markTouched("email"),
                        onChange: (event) => setEmail(event.target.value),
                      }}
                    />
                    {touched.email && fieldErrors.email ? (
                      <FormHelperText error>{fieldErrors.email}</FormHelperText>
                    ) : null}
                  </GridItem>
                  <GridItem xs={12} sm={12} md={3}>
                    <CustomInput
                      labelText="Gst Number"
                      id="gstNumber"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      inputProps={{
                        value: gstNumber,
                        onChange: (event) => setGstNumber(event.target.value),
                      }}
                    />
                  </GridItem>

                  {userInfo.role === "admin" && (
                    <GridItem xs={6} sm={6} md={3} style={{ alignSelf: "end" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            tabIndex={-1}
                            onChange={(e) => {
                              setCashPreference(e.target.checked);
                            }}
                            value={cashPreference}
                            checkedIcon={
                              <Check className={classes.checkedIcon} />
                            }
                            icon={<Check className={classes.uncheckedIcon} />}
                            classes={{
                              checked: classes.checked,
                            }}
                          />
                        }
                        label="Cash Preference"
                      />



                    </GridItem>
                  )}
                  <GridItem xs={6} sm={6} md={3}>
                    <CustomInput
                      labelText="Balance Lock"
                      id="balanceLock"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      inputProps={{
                        value: balanceLock,
                        onChange: (event) => setBalanceLock(event.target.value),
                        type: "number",
                      }}
                    />
                    <FormHelperText>
                      If running balance exceeds this, challan/order/bill will be blocked.
                    </FormHelperText>
                  </GridItem>
                  {/* {userInfo.role === "admin" && (
                    <GridItem xs={6} sm={6} md={6}>
                      <CustomInput
                        labelText="Threshold Balance"
                        id="thresholdBalance"
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          value: thresholdBalance,
                          onChange: (event) =>
                            setThresholdBalance(event.target.value),
                        }}
                      />
                    </GridItem>
                  )} */}
                  {/* {userInfo.role === "admin" && (
                    <GridItem xs={12} sm={12} md={4}>
                      <CustomInput
                        labelText="Open Balance"
                        id="openBalance"
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          value: openBalance,
                          onChange: (event) =>
                            setOpenBalance(event.target.value),
                        }}
                      />
                    </GridItem>
                  )} */}
                  {/* {userInfo.role === "admin" && (
                    <GridItem xs={12} sm={12} md={4}>
                      <CustomInput
                        labelText="Current Balance"
                        id="currentBalance"
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          value: currentBalance,
                          onChange: (event) =>
                            setCurrentBalance(event.target.value),
                        }}
                      />
                    </GridItem>
                  )} */}
                  {/* {userInfo.role === "admin" && (
                    <GridItem xs={12} sm={12} md={4}>
                      <CustomInput
                        labelText="Last Year Balance"
                        id="lastYearBalance"
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          value: lastYearBalance,
                          onChange: (event) =>
                            setLastYearBalance(event.target.value),
                        }}
                      />
                    </GridItem>
                  )} */}
                  <GridItem xs={12} sm={12} md={3}>
                    <Autocomplete
                      id="challanType"
                      className={classes.formControl}
                      options={["challan", "A6", "Thermal"]}
                      value={challanType}
                      onChange={(event, value) => {
                        setChallanType(value);
                      }}
                      getOptionLabel={(option) => (option ? option : "")}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Challan Type"
                        />
                      )}
                    />
                  </GridItem>
                  {/* <GridItem xs={12} sm={12} md={2}>
                    <CustomInput
                      labelText="Opening Balance"
                      id="pin"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      inputProps={{
                        value: openBalance,
                        onChange: (event) => setOpenBalance(event.target.value),
                      }}
                    />
                  </GridItem> */}
                  <GridItem xs={12} sm={12} md={12} style={{ alignSelf: "end", flexDirection: 'row' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          tabIndex={-1}
                          onChange={(e) => {
                            setShowRunningBalance(e.target.checked);
                          }}
                          value={showRunningBalance}
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          classes={{
                            checked: classes.checked,
                          }}
                        />
                      }
                      label="Show Running Balance"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          tabIndex={-1}
                          onChange={(e) => {
                            setIsELigibleForIgst(e.target.checked);
                          }}
                          value={cashPreference}
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          classes={{
                            checked: classes.checked,
                          }}
                        />
                      }
                      label="Is Eligible for Igst"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          tabIndex={-1}
                          onChange={(e) => {
                            setShowPrice(e.target.checked);
                          }}
                          value={showPrice}
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          classes={{
                            checked: classes.checked,
                          }}
                        />
                      }
                      label="Show Price in Printables"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          tabIndex={-1}
                          onChange={(e) => {
                            setPrintBulk(e.target.checked);
                          }}
                          checked={printBulk}
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          classes={{
                            checked: classes.checked,
                          }}
                        />
                      }
                      label="Include in Bulk Print"
                    />
                  </GridItem>
                  {shippingAddress.allAddress.map((i, index) => (
                    <React.Fragment key={`ship-${index}`}>
                      <GridContainer
                        spacing={2}
                        style={{ margin: 0, width: "90%" }}
                      >
                        <GridItem xs={12} sm={12} md={2}>
                          <CustomInput
                            labelText="Full Name"
                            id="address"
                            formControlProps={{
                              fullWidth: true,
                            }}
                            inputProps={{
                              multiline: true,
                              value: i.fullname,
                              onChange: (event) =>
                                updateShippingRow(index, {
                                  fullname: event.target.value,
                                }),
                            }}
                          />
                        </GridItem>
                        <GridItem xs={12} sm={12} md={2}>
                          <CustomInput
                            labelText="Shipping Address"
                            id="shippingAddress"
                            formControlProps={{
                              fullWidth: true,
                            }}
                            inputProps={{
                              multiline: true,
                              value: i.address,
                              min: 0,
                              onChange: (event) => {
                                updateShippingRow(index, {
                                  address: event.target.value,
                                });
                              },
                            }}
                          />
                        </GridItem>

                        <GridItem xs={12} sm={12} md={2}>
                          <Autocomplete
                            id="state"
                            className={classes.formControl}
                            options={states}
                            value={i.stateAddress}
                            onChange={(event, value) => {
                              updateShippingRow(index, { stateAddress: value, city: "" });
                            }}
                            getOptionLabel={(option) => (option ? option : "")}
                            renderInput={(params) => (
                              <TextField {...params} label="Select State" />
                            )}
                          />
                        </GridItem>
                        <GridItem xs={12} sm={12} md={2}>
                          <Autocomplete
                            id="city"
                            className={classes.formControl}
                            options={StatePicker.getCities("IN", i.stateAddress || "") || []}
                            value={i.city}
                            onChange={(event, value) => {
                              updateShippingRow(index, { city: value });
                            }}
                            getOptionLabel={(option) => (option ? option : "")}
                            renderInput={(params) => (
                              <TextField {...params} label="Select City" />
                            )}
                          />
                        </GridItem>
                        <GridItem xs={12} sm={12} md={2}>
                          <CustomInput
                            labelText="Pincode"
                            id="pin"
                            formControlProps={{
                              fullWidth: true,
                            }}
                            inputProps={{
                              value:
                                i.pincode != null && i.pincode !== ""
                                  ? i.pincode
                                  : i.pin || "",
                              onChange: (event) =>
                                updateShippingRow(index, {
                                  pincode: event.target.value,
                                }),
                            }}
                          />
                        </GridItem>
                        {index ? (
                          <GridItem
                            xs={12}
                            sm={12}
                            md={1}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Button
                              color="secondary"
                              onClick={() => removeShippingAddressInput(index)}
                            >
                              Remove
                            </Button>
                          </GridItem>
                        ) : null}
                      </GridContainer>
                    </React.Fragment>
                  ))}
                  <GridItem
                    xs={12}
                    sm={12}
                    md={1}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Button color="primary" onClick={addShippingAddressInput}>
                      Add
                    </Button>
                  </GridItem>
                </GridContainer>
              </CardBody>
            </>
          ) : null}
          {activeStep === 1 ? (
            <CardBody>
              <GridContainer>
                <GridItem xs={6} sm={6} md={2}>
                  <CustomInput
                    labelText="Employee Name"
                    id="EmployeeName"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    error={Boolean(employeeTouched.employeeName && employeeFieldErrors.employeeName)}
                    inputProps={{
                      value: employeeName,
                      onBlur: () => markEmployeeTouched("employeeName"),
                      onChange: (event) => setEmployeeName(event.target.value),
                    }}
                  />
                  {employeeTouched.employeeName && employeeFieldErrors.employeeName ? (
                    <FormHelperText error>{employeeFieldErrors.employeeName}</FormHelperText>
                  ) : null}
                </GridItem>
                <GridItem xs={6} sm={6} md={2}>
                  <CustomInput
                    labelText="Employee Email"
                    id="EmployeeEmail"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    error={Boolean(employeeTouched.employeeEmail && employeeFieldErrors.employeeEmail)}
                    inputProps={{
                      value: employeeEmail,
                      onBlur: () => markEmployeeTouched("employeeEmail"),
                      onChange: (event) => setEmployeeEmail(event.target.value),
                    }}
                  />
                  {employeeTouched.employeeEmail && employeeFieldErrors.employeeEmail ? (
                    <FormHelperText error>{employeeFieldErrors.employeeEmail}</FormHelperText>
                  ) : null}
                </GridItem>
                <GridItem xs={6} sm={6} md={2}>
                  <CustomInput
                    labelText="Employee Password"
                    id="EmployeePassword"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    error={Boolean(employeeTouched.employeePassword && employeeFieldErrors.employeePassword)}
                    inputProps={{
                      value: employeePassword,
                      onBlur: () => markEmployeeTouched("employeePassword"),
                      onChange: (event) => setEmployeePassword(event.target.value),
                    }}
                  />
                  {employeeTouched.employeePassword && employeeFieldErrors.employeePassword ? (
                    <FormHelperText error>{employeeFieldErrors.employeePassword}</FormHelperText>
                  ) : null}
                </GridItem>
                <GridItem xs={6} sm={6} md={2}>
                  <CustomInput
                    labelText="Employee Mobile"
                    id="EmployeeMobile"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    error={Boolean(employeeTouched.employeeMobile && employeeFieldErrors.employeeMobile)}
                    inputProps={{
                      value: employeeMobile,
                      onBlur: () => markEmployeeTouched("employeeMobile"),
                      onChange: (event) => setEmployeeMobile(event.target.value),
                    }}
                  />
                  {employeeTouched.employeeMobile && employeeFieldErrors.employeeMobile ? (
                    <FormHelperText error>{employeeFieldErrors.employeeMobile}</FormHelperText>
                  ) : null}
                </GridItem>
                <GridItem xs={12} sm={12} md={4}>
                  <CustomInput
                    labelText="Employee Address"
                    id="EmployeeAddress"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      value: employeeAddress,
                      onChange: (event) =>
                        setEmployeeAddress(event.target.value),
                    }}
                  />
                </GridItem>
                <GridItem
                  xs={12}
                  sm={12}
                  md={2}
                  style={{ display: "flex", alignItems: "flex-end" }}
                >
                  <FormControlLabel
                    label="Add As Admin"
                    control={
                      <Checkbox
                        checked={employeeRole}
                        onChange={(e) => setEmployeeRole(e.target.checked)}
                      />
                    }
                  />
                </GridItem>

                <GridItem xs={12} sm={12} md={12}>
                  <FormControlLabel
                    label="Select All"
                    control={
                      <Checkbox
                        checked={selectAll}
                        onChange={(e) => {
                          setSelectAll(e.target.checked);
                          setViewDashboard(e.target.checked);
                          setViewSystemUsers(e.target.checked);
                          setViewAccountUsers(e.target.checked);

                          setViewPurchase(e.target.checked);
                          setViewAccounts(e.target.checked);
                          setViewAccountsVerification(e.target.checked);
                          setViewDiscounts(e.target.checked);
                          setViewOrders(e.target.checked);
                          setViewOrdersApp(e.target.checked);
                          setViewOrderReturn(e.target.checked);
                          setViewMaster(e.target.checked);
                          setViewTransaction(e.target.checked);
                          setViewOrderStatus(e.target.checked);
                          setViewAccounting(e.target.checked);
                          setViewPrintAd(e.target.checked);
                          setViewBankList(e.target.checked);
                          setViewPaymentList(e.target.checked);
                          setViewPurchaseBarcode(e.target.checked);
                          setViewProductBarcode(e.target.checked);
                          setViewOrderPrintPage(e.target.checked);
                          setViewBrandsList(e.target.checked);
                          setViewCategoryList(e.target.checked);
                          setViewOfferList(e.target.checked);
                          setViewTrendingList(e.target.checked);
                          setViewBrochureList(e.target.checked);
                          setViewPriceList(e.target.checked);
                        }}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View Dashboard"
                    control={
                      <Checkbox
                        checked={viewDashboard}
                        onChange={(e) => setViewDashboard(e.target.checked)}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View System Users"
                    control={
                      <Checkbox
                        checked={viewSystemUsers}
                        onChange={(e) => setViewSystemUsers(e.target.checked)}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View Account Users"
                    control={
                      <Checkbox
                        checked={viewAccountUsers}
                        onChange={(e) => setViewAccountUsers(e.target.checked)}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View Purchase"
                    control={
                      <Checkbox
                        checked={viewPurchase}
                        onChange={(e) => setViewPurchase(e.target.checked)}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View Accounts"
                    control={
                      <Checkbox
                        checked={viewAccounts}
                        onChange={(e) => setViewAccounts(e.target.checked)}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View AccountsVerification"
                    control={
                      <Checkbox
                        checked={viewAccountsVerification}
                        onChange={(e) =>
                          setViewAccountsVerification(e.target.checked)
                        }
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View Discounts"
                    control={
                      <Checkbox
                        checked={viewDiscounts}
                        onChange={(e) => setViewDiscounts(e.target.checked)}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View Orders"
                    control={
                      <Checkbox
                        checked={viewOrders}
                        onChange={(e) => setViewOrders(e.target.checked)}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View OrdersApp"
                    control={
                      <Checkbox
                        checked={viewOrdersApp}
                        onChange={(e) => setViewOrdersApp(e.target.checked)}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View Orders Return"
                    control={
                      <Checkbox
                        checked={viewOrderReturn}
                        onChange={(e) => setViewOrderReturn(e.target.checked)}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View Master"
                    control={
                      <Checkbox
                        checked={viewMaster}
                        onChange={(e) => setViewMaster(e.target.checked)}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View Transaction"
                    control={
                      <Checkbox
                        checked={viewTransaction}
                        onChange={(e) => setViewTransaction(e.target.checked)}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View OrderStatus"
                    control={
                      <Checkbox
                        checked={viewOrderStatus}
                        onChange={(e) => setViewOrderStatus(e.target.checked)}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View Accounting"
                    control={
                      <Checkbox
                        checked={viewAccounting}
                        onChange={(e) => setViewAccounting(e.target.checked)}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View PrintAd"
                    control={
                      <Checkbox
                        checked={viewPrintAd}
                        onChange={(e) => setViewPrintAd(e.target.checked)}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View BankList"
                    control={
                      <Checkbox
                        checked={viewBankList}
                        onChange={(e) => setViewBankList(e.target.checked)}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View PaymentList"
                    control={
                      <Checkbox
                        checked={viewPaymentList}
                        onChange={(e) => setViewPaymentList(e.target.checked)}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View Purchase Barcode"
                    control={
                      <Checkbox
                        checked={viewPurchaseBarcode}
                        onChange={(e) =>
                          setViewPurchaseBarcode(e.target.checked)
                        }
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View Product Barcode"
                    control={
                      <Checkbox
                        checked={viewProductBarcode}
                        onChange={(e) =>
                          setViewProductBarcode(e.target.checked)
                        }
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View Order Print Page "
                    control={
                      <Checkbox
                        checked={viewOrderPrintPage}
                        onChange={(e) =>
                          setViewOrderPrintPage(e.target.checked)
                        }
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View BrandsList"
                    control={
                      <Checkbox
                        checked={viewBrandsList}
                        onChange={(e) => setViewBrandsList(e.target.checked)}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View CategoryList"
                    control={
                      <Checkbox
                        checked={viewCategoryList}
                        onChange={(e) => setViewCategoryList(e.target.checked)}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View OfferList"
                    control={
                      <Checkbox
                        checked={viewOfferList}
                        onChange={(e) => setViewOfferList(e.target.checked)}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View TrendingList"
                    control={
                      <Checkbox
                        checked={viewTrendingList}
                        onChange={(e) => setViewTrendingList(e.target.checked)}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View BrochureList"
                    control={
                      <Checkbox
                        checked={viewBrochureList}
                        onChange={(e) => setViewBrochureList(e.target.checked)}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={3}>
                  <FormControlLabel
                    label="View PriceList"
                    control={
                      <Checkbox
                        checked={viewPriceList}
                        onChange={(e) => setViewPriceList(e.target.checked)}
                      />
                    }
                  />
                </GridItem>
                <GridItem xs={12}>
                  <Fab
                    size={"small"}
                    color="primary"
                    aria-label="add"
                    disabled={!canAddEmployee}
                    onClick={() => {
                      ["employeeName", "employeeMobile", "employeeEmail", "employeePassword"].forEach(
                        markEmployeeTouched
                      );
                      if (!employeeName || !String(employeeName).trim()) {
                        toast.error("Please enter employee name.", {
                          position: "top-center",
                          autoClose: 2500,
                        });
                        return;
                      }
                      if (!isValidIndianMobile(employeeMobile)) {
                        toast.error("Please enter a valid 10-digit employee mobile.", {
                          position: "top-center",
                          autoClose: 2500,
                        });
                        return;
                      }
                      if (!employeeEmail || !EMAIL_RE.test(String(employeeEmail).trim())) {
                        toast.error("Please enter a valid employee email.", {
                          position: "top-center",
                          autoClose: 2500,
                        });
                        return;
                      }
                      if (!employeePassword || !String(employeePassword).trim()) {
                        toast.error("Please enter employee password.", {
                          position: "top-center",
                          autoClose: 2500,
                        });
                        return;
                      }
                      let data = {
                        name: employeeName,
                        email: employeeEmail,
                        mobile: employeeMobile,
                        address: employeeAddress,
                        password: employeePassword,
                        role: employeeRole ? "admin" : "user",
                        viewDashboard,
                        viewSystemUsers,
                        viewPurchase,
                        viewAccounts,
                        viewAccountsVerification,
                        viewDiscounts,
                        viewOrders,
                        viewOrdersApp,
                        viewOrderReturn,
                        viewMaster,
                        viewTransaction,
                        viewOrderStatus,
                        viewAccounting,
                        viewPrintAd,
                        viewBankList,
                        viewPaymentList,
                        viewPurchaseBarcode,
                        viewProductBarcode,
                        viewOrderPrintPage,
                        viewBrandsList,
                        viewCategoryList,
                        viewOfferList,
                        viewTrendingList,
                        viewBrochureList,
                        viewPriceList,
                        isAccountVerified: true,
                      };
                      setEmployees((prev) => [...prev, data]);
                      setEmployeeName("");
                      setEmployeeEmail("");
                      setEmployeePassword("");
                      setEmployeeMobile("");
                      setEmployeeAddress("");
                      setEmployeeTouched({});
                      setSelectAll(null);
                      setViewDashboard(null);
                      setViewSystemUsers(null);
                      setViewAccountUsers(null);
                      setViewPurchase(null);
                      setViewAccounts(null);
                      setViewAccountsVerification(null);
                      setViewDiscounts(null);
                      setViewOrders(null);
                      setViewOrdersApp(null);
                      setViewOrderReturn(null);
                      setViewMaster(null);
                      setViewTransaction(null);
                      setViewOrderStatus(null);
                      setViewAccounting(null);
                      setViewPrintAd(null);
                      setViewBankList(null);
                      setViewPaymentList(null);
                      setViewPurchaseBarcode(null);
                      setViewProductBarcode(null);
                      setViewOrderPrintPage(null);
                      setViewBrandsList(null);
                      setViewCategoryList(null);
                      setViewOfferList(null);
                      setViewTrendingList(null);
                      setViewBrochureList(null);
                      setViewPriceList(null);
                      setEmployeeRole(false);
                    }}
                  >
                    <AddIcon />
                  </Fab>
                </GridItem>
                <GridItem xs={12}>
                  <Table stickyHeader aria-label="sticky table">
                    <TableHead>
                      <TableRow>
                        {accountUsersColumns.map((column) => (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            style={{ minWidth: column.minWidth }}
                          >
                            {column.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {employees.length > 0 &&
                        employees.map((row, index) => {
                          return (
                            <TableRow
                              hover
                              role="checkbox"
                              tabIndex={-1}
                              key={row.code}
                            >
                              {accountUsersColumns.map((column) => {
                                const value = row[column.id];
                                if (value && column.id === "leaderId") {

                                  return (
                                    <TableCell
                                      key={column.id}
                                      align={column.align}
                                    >
                                      {column.format &&
                                        typeof value === "number"
                                        ? column.format(value.name)
                                        : value.name}
                                    </TableCell>
                                  );
                                }

                                if (column.id === "permissions") {
                                  let {
                                    viewDashboard,
                                    viewSystemUsers,
                                    viewPurchase,
                                    viewAccounts,
                                    viewAccountsVerification,
                                    viewDiscounts,
                                    viewOrders,
                                    viewOrdersApp,
                                    viewOrderReturn,
                                    viewMaster,
                                    viewTransaction,
                                    viewOrderStatus,
                                    viewAccounting,
                                    viewPrintAd,
                                    viewBankList,
                                    viewPaymentList,
                                    viewPurchaseBarcode,
                                    viewProductBarcode,
                                    viewOrderPrintPage,
                                    viewBrandsList,
                                    viewCategoryList,
                                    viewOfferList,
                                    viewTrendingList,
                                    viewBrochureList,
                                    viewPriceList,
                                  } = row;
                                  return (
                                    <TableCell>
                                      <GridContainer>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View Dashboard"
                                            control={
                                              <Checkbox
                                                checked={viewDashboard}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View System Users"
                                            control={
                                              <Checkbox
                                                checked={viewSystemUsers}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View Purchase"
                                            control={
                                              <Checkbox
                                                checked={viewPurchase}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View Accounts"
                                            control={
                                              <Checkbox
                                                checked={viewAccounts}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View AccountsVerification"
                                            control={
                                              <Checkbox
                                                checked={
                                                  viewAccountsVerification
                                                }
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View Discounts"
                                            control={
                                              <Checkbox
                                                checked={viewDiscounts}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View Orders"
                                            control={
                                              <Checkbox
                                                checked={viewOrders}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View OrdersApp"
                                            control={
                                              <Checkbox
                                                checked={viewOrdersApp}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View Orders Return"
                                            control={
                                              <Checkbox
                                                checked={viewOrderReturn}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View Master"
                                            control={
                                              <Checkbox
                                                checked={viewMaster}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View Transaction"
                                            control={
                                              <Checkbox
                                                checked={viewTransaction}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View OrderStatus"
                                            control={
                                              <Checkbox
                                                checked={viewOrderStatus}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View Accounting"
                                            control={
                                              <Checkbox
                                                checked={viewAccounting}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View PrintAd"
                                            control={
                                              <Checkbox
                                                checked={viewPrintAd}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View BankList"
                                            control={
                                              <Checkbox
                                                checked={viewBankList}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View PaymentList"
                                            control={
                                              <Checkbox
                                                checked={viewPaymentList}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View Purchase Barcode"
                                            control={
                                              <Checkbox
                                                checked={viewPurchaseBarcode}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View Product Barcode"
                                            control={
                                              <Checkbox
                                                checked={viewProductBarcode}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View Order Print Page "
                                            control={
                                              <Checkbox
                                                checked={viewOrderPrintPage}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View BrandsList"
                                            control={
                                              <Checkbox
                                                checked={viewBrandsList}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View CategoryList"
                                            control={
                                              <Checkbox
                                                checked={viewCategoryList}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View OfferList"
                                            control={
                                              <Checkbox
                                                checked={viewOfferList}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View TrendingList"
                                            control={
                                              <Checkbox
                                                checked={viewTrendingList}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View BrochureList"
                                            control={
                                              <Checkbox
                                                checked={viewBrochureList}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                        <GridItem xs={6} sm={6} md={3}>
                                          <FormControlLabel
                                            label="View PriceList"
                                            control={
                                              <Checkbox
                                                checked={viewPriceList}
                                                color="default"
                                              />
                                            }
                                          />
                                        </GridItem>
                                      </GridContainer>
                                    </TableCell>
                                  );
                                }

                                return (
                                  <TableCell
                                    key={column.id}
                                    align={column.align}
                                  >
                                    {column.format && typeof value === "number"
                                      ? column.format(value)
                                      : value}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </GridItem>
              </GridContainer>
            </CardBody>
          ) : null}
          {/* {activeStep === 2 ? (
            <CardBody>
              <GridContainer>
                <GridItem xs={12} sm={12} md={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        tabIndex={-1}
                        onChange={(e) => {
                          setCopyDiscount(e.target.checked);
                          if (e.target.checked) {
                            setOpenPartySelectionDialog(true);
                          }
                        }}
                        value={copyDiscount}
                        checkedIcon={<Check className={classes.checkedIcon} />}
                        icon={<Check className={classes.uncheckedIcon} />}
                        classes={{
                          checked: classes.checked,
                        }}
                      />
                    }
                    label="Copy From Company"
                  />
                </GridItem>
                <GridItem xs={3}>
                  <Autocomplete
                    filterOptions={filterOptions}
                    id="product"
                    className={classes.formControl}
                    options={products}
                    loading={products.length === 0 ? true : false}
                    value={product}
                    onChange={(event, value) => {
                      setProduct(value);
                    }}
                    getOptionLabel={(option) =>
                      option && option.productName
                        ? option.productName + " (" + option.productCode + ")"
                        : ""
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Select Product" />
                    )}
                  />
                </GridItem>
                <GridItem xs={3}>
                  <CustomInput
                    labelText="Price"
                    id="price"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      disabled: true,
                      value: product ? (product?.rate ? product.rate : 0) : "",
                    }}
                  />
                </GridItem>
                <GridItem xs={3}>
                  <CustomInput
                    labelText="Discount Percent"
                    id="discountPercent"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      type: "number",
                      value: discountPercent,
                      onChange: (event) => {
                        let discountPercent = event.target.value;
                        setDiscountPercent(discountPercent);
                        let price = product ? product.rate : 0;
                        let discountPrice =
                          price - (discountPercent / 100) * price;
                        setDiscountedPrice(discountPrice);
                      },
                    }}
                  />
                </GridItem>

                <GridItem xs={2}>
                  <CustomInput
                    labelText="Discounted Price"
                    id="discountedPrice"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      disabled: true,
                      value: discountedPrice,
                    }}
                  />
                </GridItem>
                <GridItem xs={1}>
                  <Fab
                    size={"small"}
                    color="primary"
                    aria-label="add"
                    onClick={() => {
                      if (product === "") {
                        return alert("Please select product");
                      }
                      if (discountPercent === "") {
                        return alert("Please enter discount percent");
                      }
                      if (discountedPrice === "") {
                        return alert("Please discount amount");
                      }
                      let data = {
                        price: product?.rate,
                        product,
                        discountPercent,
                        discountedPrice,
                      };
                      discounts.push(data);
                      setProduct(null);
                      setDiscountedPrice("");
                      setDiscountPercent("");
                    }}
                  >
                    <AddIcon />
                  </Fab>
                </GridItem>
                {_.isEmpty(discounts) ? null : (
                  <GridItem xs={12}>
                    <Table stickyHeader aria-label="sticky table">
                      <TableHead>
                        <TableRow>
                          {columns.map((column) => (
                            <TableCell
                              key={column.id}
                              align={column.align}
                              style={{ minWidth: column.minWidth }}
                            >
                              {column.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {discounts.map((row, index) => {
                          return (
                            <TableRow
                              hover
                              role="checkbox"
                              tabIndex={-1}
                              key={row.code}
                            >
                              {columns.map((column) => {
                                let value = row[column.id];
                                if (column.id === "product") {
                                  value = value.productName;
                                }
                                if (column.id === "action") {
                                  return (
                                    <TableCell align={"center"}>
                                      <Box
                                        display={"flex"}
                                        justifyContent={"center"}
                                      >
                                        <IconButton
                                          aria-label="delete"
                                          onClick={() => {
                                            let copy = _.clone(discounts);
                                            copy.splice(index, 1);
                                            setDiscounts(copy);
                                          }}
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </Box>
                                    </TableCell>
                                  );
                                }
                                return (
                                  <TableCell
                                    key={column.id}
                                    align={column.align}
                                  >
                                    {column.format && typeof value === "number"
                                      ? column.format(value)
                                      : value}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </GridItem>
                )}
              </GridContainer>
            </CardBody>
          ) : null} */}
        </Card>
        <GridContainer>
          <GridItem xs={12}>
            <Box
              display={"flex"}
              margin={4}
              justifyContent={"center"}
              alignItems={"center"}
            >
              {activeStep === steps.length ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={_onSave}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Adding..." : "Add account"}
                </Button>
              ) : (
                <Box
                  flexDirection={"column"}
                  display={"flex"}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  {/*<Typography className={classes.instructions}>{getStepContent(activeStep)}</Typography>*/}
                  <Box
                    display={"flex"}
                    justifyContent={"center"}
                    alignItems={"center"}
                  >
                    <Button
                      disabled={activeStep === 0}
                      onClick={handleBack}
                      className={classes.backButton}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleNext}
                      disabled={activeStep === 0 ? !isStep0Valid : false}
                    >
                      {activeStep === steps.length - 1 ? "Finish" : "Next"}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </GridItem>
        </GridContainer>
      </div>
      <PartySelectionDialog />
    </Dialog>
  );
}
